import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  Container,
  Navbar,
  NavbarBrand,
  Input,
  Label,
  Form,
  FormGroup,
  FormFeedback,
  FormText,
  Select,
  SelectOption,
  Checkbox,
  Button,
} from 'components/apollo';
import { Row, Column } from 'components/grid';
import PhoneInput, { PhoneValidMsg } from 'components/inputs/phone-input2';
import CountrySelect from 'components/selects/country-select';
import CitySelect from 'components/selects/city-select';
import DatePicker from 'components/common/date-picker/date-picker';
import SettingsTooltip from '../../../settings/components/settings-tooltip';
import GoogleRecaptcha from 'components/vendor/google/google-recaptcha';
import RegisterColumns from '../../common/register-columns';
import Validation from '../../common/validation';
import CommonValidation from 'utilities/validations/validation';
import RegisterSuccess from '../../components/register-partner-success';
import RegisterError from '../../components/register-error';
import { fetchRegion } from '../../../../redux/services/api/region/region-services';
import iconImage from '../register-partner/AFRY-Logotype-Horizontal_White.svg';
import './register-partner-v2.css';

const civicBirthDateCountries = ['SE', 'NO', 'DK', 'FI'];

const propTypes = {
  register: PropTypes.func.isRequired,
  success: PropTypes.bool.isRequired,
  companydomains: PropTypes.arrayOf(PropTypes.string),
  getAllCompanyDomains: PropTypes.func.isRequired,
  privacyStatementForNetworkers: PropTypes.string.isRequired,
  portalUrl: PropTypes.string.isRequired,
  params: PropTypes.shape({
    languageCode: PropTypes.string,
  }),
};

const defaultProps = {
  companydomains: [],
  params: {},
};

const contentCols = {
  mobile: { size: 12, offset: 0 },
  tablet: { size: 12, offset: 0 },
  desktop: { size: 10, offset: 1 },
  largeDesktop: { size: 10, offset: 1 },
};

class RegisterPartnerV2 extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Email step
      email: '',
      emailConfirmed: false,
      emailError: null,

      // Personal details
      firstName: '',
      lastName: '',
      birthday: '',
      civicBirthDate: '',
      civicLastDigits: '',
      selectedCivic: Validation.civicLastDigits.SE.locale,
      phoneMobile: '',
      phoneNumberValid: PhoneValidMsg.required,
      phoneCountry: 'SE',
      homeCountryId: '',
      countryName: '',
      geoLocationKey: '',
      streetAddress: '',
      zipCode: '',
      careOf: '',
      linkedin: '',
      region: '',

      // Company details
      companyNotStarted: false,
      organizationNumber: '',
      companyName: '',
      webpage: '',

      // Communications & sales
      communicationResponsible: true,
      salesManager: '',
      phoneMobileSalesManager: '',
      phoneMobileSalesValid: null,
      emailSalesManager: '',

      // Bottom
      privacyStatement: false,
      captchaCompleted: false,
      showErrors: false,
    };
  }

  componentDidMount() {
    const { getAllCompanyDomains } = this.props;
    getAllCompanyDomains();
  }

  // ─── Email step ──────────────────────────────────────────────────────────────

  handleEmailChange = (e) => {
    this.setState({ email: e.target.value.trim(), emailError: null });
  };

  handleContinue = () => {
    const { email } = this.state;
    const { companydomains } = this.props;

    if (!CommonValidation.Email.allowedEmailAndDomain(email, companydomains || [])) {
      this.setState({ emailError: 'Please enter a valid email address' });
      return;
    }

    this.setState({ emailConfirmed: true });
  };

  // ─── Personal details ────────────────────────────────────────────────────────

  handleChange = async (e) => {
    const { name, value, checked, type, label } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (name === 'homeCountryId') {
      this.setState({
        homeCountryId: val,
        countryName: label || '',
        geoLocationKey: '',
        region: '',
      });
      return;
    }

    if (name === 'geoLocationKey') {
      this.setState({ geoLocationKey: val });
      const { countryName } = this.state;
      fetchRegion(countryName, label || val).then((res) => {
        const regionName =
          res.data && res.data.length > 0 && res.data[0] !== null ? res.data[0].name : '';
        this.setState({ region: regionName });
      });
      return;
    }

    this.setState({ [name]: val });
  };

  handleBirthdateChange = (number) => {
    const format = moment()._locale._longDateFormat.L.split('');
    let temp = number;
    if (
      number.length !== format.length &&
      format[number.length - 1] !== format[number.length]
    ) {
      temp += format[number.length];
    }
    const { selectedCivic } = this.state;
    const civicBirthDate = civicBirthDateCountries.includes(selectedCivic)
      ? Validation.civicLastDigits[selectedCivic].dateFormat(temp)
      : Validation.civicLastDigits.otherCountry.dateFormat(temp);
    this.setState({ birthday: temp, civicBirthDate });
  };

  handleCivicCountryChange = (e) => {
    const { phoneCountry, selectedCivic } = this.state;
    if (!phoneCountry) {
      this.setState({ phoneCountry: selectedCivic });
    }
    this.setState({ selectedCivic: e.target.value });
  };

  handleCivicLastDigitsChange = (e) => {
    const { selectedCivic } = this.state;
    const limit = civicBirthDateCountries.includes(selectedCivic)
      ? Validation.civicLastDigits[selectedCivic].digitlength
      : Validation.civicLastDigits.otherCountry.digitlength;
    if (e.target.value.length <= limit) {
      this.setState({ civicLastDigits: e.target.value });
    }
  };

  handlePhoneChange = (number) => {
    this.setState({ phoneMobile: number });
  };

  handlePhoneValid = (validMsg) => {
    if (this.state.phoneNumberValid !== validMsg) {
      this.setState({ phoneNumberValid: validMsg });
    }
  };

  // ─── Communications & sales ──────────────────────────────────────────────────

  handleSalesPhoneChange = (number) => {
    this.setState({ phoneMobileSalesManager: number });
  };

  handleSalesPhoneValid = (validMsg) => {
    if (this.state.phoneMobileSalesValid !== validMsg) {
      this.setState({ phoneMobileSalesValid: validMsg });
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  handleCaptchaSuccess = () => {
    this.setState({ captchaCompleted: true });
  };

  handleSubmit = () => {
    const { register, params, portalUrl } = this.props;
    const {
      email,
      firstName, lastName, birthday, civicBirthDate, civicLastDigits, selectedCivic,
      phoneMobile, phoneNumberValid, homeCountryId, geoLocationKey,
      streetAddress, zipCode, careOf, linkedin,
      companyNotStarted, organizationNumber, companyName, webpage,
      communicationResponsible, salesManager, phoneMobileSalesManager, phoneMobileSalesValid,
      emailSalesManager,
      privacyStatement, captchaCompleted,
    } = this.state;

    const personValid =
      Validation.Person.firstName(firstName) &&
      Validation.Person.lastName(lastName) &&
      Validation.Person.birthDay(birthday) &&
      phoneNumberValid === PhoneValidMsg.valid &&
      Validation.Person.geoLocationKey(geoLocationKey) &&
      Validation.Person.zipCode(zipCode) &&
      Validation.Person.streetAddress(streetAddress) &&
      Validation.Person.country(homeCountryId) &&
      Validation.Person.linkedin(linkedin) &&
      Validation.Person.civicRegNumberLast(civicLastDigits, selectedCivic) &&
      Validation.Person.civicRegNumberCheckSum(civicBirthDate, civicLastDigits, selectedCivic);

    const partnerValid =
      Validation.Partner.organizationNumber(organizationNumber) &&
      Validation.Partner.companyName(companyName) &&
      Validation.Partner.webpage(webpage) &&
      Validation.Partner.salesManager(communicationResponsible, salesManager) &&
      Validation.Partner.phoneMobileSalesManager(
        communicationResponsible,
        phoneMobileSalesValid === PhoneValidMsg.valid
      ) &&
      Validation.Partner.emailSalesManager(communicationResponsible, emailSalesManager);

    const captchaValid =
      portalUrl === 'https://portalstage.afry.cloud/' || captchaCompleted;

    if (!personValid || !partnerValid || !privacyStatement || !captchaValid) {
      this.setState({ showErrors: true });
      return;
    }

    const languageCode = (params && params.languageCode) || 'sv';

    register({
      personal: {
        firstName,
        lastName,
        birthday,
        civicBirthDate,
        civicLastDigits,
        selectedCivic,
        phoneMobile: `+${phoneMobile}`,
        homeCountryId,
        geoLocationKey,
        streetAddress,
        zipCode,
        careOf,
        linkedin,
        header: 'Partner',
        selectedLanguage: languageCode,
      },
      partner: {
        companyNotStarted,
        organizationNumber,
        companyName,
        webpage,
        communicationResponsible,
        salesManager: communicationResponsible ? '' : salesManager,
        phoneMobileSalesManager: communicationResponsible ? '' : `+${phoneMobileSalesManager}`,
        emailSalesManager: communicationResponsible ? '' : emailSalesManager,
      },
      account: {
        email,
        languageCode,
      },
      privacyStatement,
      captchaCompleted,
    });
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  getPhoneErrorMsg = (valid) => {
    switch (valid) {
      case PhoneValidMsg.required:
        return 'Please enter your phone number';
      case PhoneValidMsg.removeZero:
        return 'Remove the first zero from number';
      default:
        return 'Phone number is invalid';
    }
  };

  renderCivicRegNumber() {
    const { showErrors, selectedCivic, civicLastDigits, civicBirthDate } = this.state;

    const invalidFormat = civicBirthDateCountries.includes(selectedCivic)
      ? !Validation.Person.civicRegNumberLast(civicLastDigits, selectedCivic)
      : !Validation.Person.civicRegNumberLast(civicLastDigits, 'otherCountry');

    const invalidChecksum = civicBirthDateCountries.includes(selectedCivic)
      ? !Validation.Person.civicRegNumberCheckSum(civicBirthDate, civicLastDigits, selectedCivic)
      : !Validation.Person.civicRegNumberCheckSum(
          civicBirthDate,
          civicLastDigits,
          'otherCountry'
        );

    const showLastDigitError = showErrors && (invalidFormat || invalidChecksum);

    return (
      <Row>
        <Column {...RegisterColumns.leftColumn}>
          <Label for="civicRegFormat">Social security number</Label>
          <FormGroup className="social-security-number-control social-security-number-country-input">
            <Select
              name="civicRegFormat"
              value={selectedCivic}
              onChange={this.handleCivicCountryChange}
            >
              <SelectOption value={Validation.civicLastDigits.SE.locale}>Sweden</SelectOption>
              <SelectOption value={Validation.civicLastDigits.NO.locale}>Norway</SelectOption>
              <SelectOption value={Validation.civicLastDigits.DK.locale}>Denmark</SelectOption>
              <SelectOption value={Validation.civicLastDigits.FI.locale}>Finland</SelectOption>
              <SelectOption value="otherCountry">Other country</SelectOption>
            </Select>
            <FormText>Select format</FormText>
          </FormGroup>
        </Column>
        <Column {...RegisterColumns.rightColumn}>
          {civicBirthDateCountries.includes(selectedCivic) && (
            <div className="social-security-last-digits">
              <div>
                <Input disabled className="civic-birthdate" value={civicBirthDate} />
                <FormText>{Validation.civicLastDigits[selectedCivic].format}</FormText>
              </div>
              <FormGroup className="civic-last">
                <Input
                  name="civicLastDigits"
                  value={civicLastDigits}
                  onChange={this.handleCivicLastDigitsChange}
                />
                <FormText>{Validation.civicLastDigits[selectedCivic].placeholder}</FormText>
                {showLastDigitError && (
                  <FormFeedback>Invalid Social security number</FormFeedback>
                )}
              </FormGroup>
            </div>
          )}
        </Column>
      </Row>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  render() {
    const { success, privacyStatementForNetworkers, portalUrl, companydomains } = this.props;
    const {
      email, emailConfirmed, emailError,
      firstName, lastName, birthday, selectedCivic,
      phoneMobile, phoneNumberValid, phoneCountry,
      homeCountryId, geoLocationKey, streetAddress, zipCode, careOf, linkedin, region,
      companyNotStarted, organizationNumber, companyName, webpage,
      communicationResponsible, salesManager, phoneMobileSalesManager, phoneMobileSalesValid,
      emailSalesManager,
      privacyStatement, captchaCompleted, showErrors,
    } = this.state;

    const wrapperClass = classNames('register-partner-v2');

    return (
      <div className={wrapperClass}>
        <Navbar dark fixed="top" expand="large">
          <Container>
            <NavbarBrand icon={iconImage} />
          </Container>
        </Navbar>

        <Container className="register-mobile-container">
          <Row>
            <Column {...contentCols}>
              <div className="register-partner-v2-content">
                {success ? (
                  <RegisterSuccess />
                ) : (
                  <Form>
                    {/* ── Header ── */}
                    <Row>
                      <Column {...RegisterColumns.fullColumn}>
                        <h2>Partner registration</h2>
                        <p>
                          Welcome and thank you for wanting to join the AFRY Partner Network!
                          Please fill out the form and submit your request to start your journey
                          as a valued Partner!
                        </p>
                        <div className="register-partner-v2-info-box">
                          <p><strong>Info! Please ensure that:</strong></p>
                          <ul>
                            <li>The email address is entered correctly - spelling etc</li>
                            <li>You have access to this personal email account since a verification email will be sent for finalizing your registration</li>
                            <li>The phone number you have entered is correct - this will be used as a step in the login process</li>
                          </ul>
                        </div>
                      </Column>
                    </Row>

                    {/* ── Email step ── */}
                    <Row>
                      <Column {...RegisterColumns.leftColumn}>
                        <FormGroup>
                          <Label for="email">Email</Label>
                          <Input
                            name="email"
                            value={email}
                            onChange={this.handleEmailChange}
                            disabled={emailConfirmed}
                          />
                          {emailError && <FormFeedback>{emailError}</FormFeedback>}
                          {showErrors &&
                            !CommonValidation.Email.allowedEmailAndDomain(
                              email,
                              companydomains || []
                            ) && <FormFeedback>Please enter a valid email</FormFeedback>}
                        </FormGroup>
                        {!emailConfirmed && (
                          <div className="register-partner-v2-continue">
                            <Button color="primary" onClick={this.handleContinue}>
                              Continue
                            </Button>
                          </div>
                        )}
                      </Column>
                    </Row>

                    {/* ── Rest of form (revealed after email confirmed) ── */}
                    {emailConfirmed && (
                      <>
                        {/* ── Contact details ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Contact details</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="firstName">First name</Label>
                              <Input
                                name="firstName"
                                onChange={this.handleChange}
                                value={firstName}
                              />
                              {showErrors && !Validation.Person.firstName(firstName) && (
                                <FormFeedback>Please enter your first name</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="lastName">Last name</Label>
                              <Input
                                name="lastName"
                                onChange={this.handleChange}
                                value={lastName}
                              />
                              {showErrors && !Validation.Person.lastName(lastName) && (
                                <FormFeedback>Please enter your last name</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="birthDate">Date of birth</Label>
                              <DatePicker
                                id="register-v2-date-picker"
                                date={birthday ? new Date(birthday) : null}
                                onDateChange={this.handleBirthdateChange}
                                placeholderText="YYYY-MM-DD"
                                dateFormat="yyyy-MM-dd"
                                startDate={false}
                              />
                              {showErrors && !Validation.Person.birthDay(birthday) && (
                                <FormFeedback>Please enter your date of birth</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        {Validation.Person.birthDay(birthday) && this.renderCivicRegNumber()}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="phoneMobile">Mobile phone</Label>
                              <PhoneInput
                                onValidChange={this.handlePhoneValid}
                                value={phoneMobile}
                                countryCode={phoneCountry ? phoneCountry.toLowerCase() : ''}
                                onChange={this.handlePhoneChange}
                              />
                              {showErrors && phoneNumberValid !== PhoneValidMsg.valid && (
                                <FormFeedback>
                                  {this.getPhoneErrorMsg(phoneNumberValid)}
                                </FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="streetAddress">Street address</Label>
                              <Input
                                name="streetAddress"
                                onChange={this.handleChange}
                                value={streetAddress}
                              />
                              {showErrors && !Validation.Person.streetAddress(streetAddress) && (
                                <FormFeedback>Please enter your street address</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="careOf">C/O (optional)</Label>
                              <Input name="careOf" onChange={this.handleChange} value={careOf} />
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="zipCode">Zip code</Label>
                              <Input
                                name="zipCode"
                                onChange={this.handleChange}
                                value={zipCode}
                              />
                              {showErrors && !Validation.Person.zipCode(zipCode) && (
                                <FormFeedback>Please enter your zip code</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="homeCountryId">Country of residence</Label>
                              <CountrySelect
                                onChange={this.handleChange}
                                selectedValue={homeCountryId || ''}
                                isDisabled={false}
                              />
                              {showErrors && !Validation.Person.country(homeCountryId) && (
                                <FormFeedback>Please select your country of residence</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="geoLocationKey">City of residence</Label>
                              {!homeCountryId && <Select disabled />}
                              {homeCountryId && (
                                <CitySelect
                                  onChange={this.handleChange}
                                  selectedValue={geoLocationKey || ''}
                                  countryKey={homeCountryId}
                                  isDisabled={false}
                                />
                              )}
                              {showErrors && !Validation.Person.geoLocationKey(geoLocationKey) && (
                                <FormFeedback>Please select your city of residence</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="region">Region</Label>
                              <Input name="region" disabled value={region} />
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="linkedin">LinkedIn (optional)</Label>
                              <Input
                                name="linkedin"
                                onChange={this.handleChange}
                                value={linkedin}
                              />
                              {showErrors && !Validation.Person.linkedin(linkedin) && (
                                <FormFeedback>Not a valid LinkedIn URL</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Company details ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Company details</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.fullColumn}>
                            <FormGroup>
                              <Label for="companyNotStarted">
                                <Checkbox
                                  id="companyNotStarted"
                                  name="companyNotStarted"
                                  onChange={this.handleChange}
                                  checked={companyNotStarted}
                                />
                                {' '}I have not started my company yet
                              </Label>
                            </FormGroup>
                          </Column>
                        </Row>
                        {companyNotStarted && (
                          <Row>
                            <Column {...RegisterColumns.fullColumn}>
                              <div className="register-partner-v2-info-box">
                                <p>
                                  You can register as a partner even though you have not started your company yet.
                                </p>
                                <p>
                                  Would you like help and support to start your company and/or with the accounting
                                  and administration? Get an offer from{' '}
                                  <a target="_blank" rel="noopener noreferrer" href="https://rebelwork.se/afry">Rebel Work</a>{' '}
                                  by sending an email to{' '}
                                  <a href="mailto:info@rebelwork.se">info@rebelwork.se</a>.
                                </p>
                              </div>
                            </Column>
                          </Row>
                        )}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="companyName">Company name</Label>
                              <Input
                                name="companyName"
                                onChange={this.handleChange}
                                value={companyName}
                              />
                              {showErrors && !Validation.Partner.companyName(companyName) && (
                                <FormFeedback>Please enter your company name</FormFeedback>
                              )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="organizationNumber">Organization number</Label>
                              <Input
                                name="organizationNumber"
                                onChange={this.handleChange}
                                value={organizationNumber}
                                maxLength="20"
                              />
                              {showErrors &&
                                !Validation.Partner.organizationNumber(organizationNumber) && (
                                  <FormFeedback>Please enter your organization number</FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="webpage">Webpage (optional)</Label>
                              <Input
                                name="webpage"
                                onChange={this.handleChange}
                                value={webpage}
                              />
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Communications & sales ── */}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <h3>Communications &amp; sales</h3>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="communicationResponsible">
                                <Checkbox
                                  id="communicationResponsible"
                                  name="communicationResponsible"
                                  onChange={this.handleChange}
                                  checked={communicationResponsible}
                                />
                                {' '}I am responsible for communication with AFRY
                                <SettingsTooltip
                                  content={[
                                    'The communication from AFRY, via email and phone, is directed to the sales responsible. Newsletters will be sent directly to the registered AFRY Network Profile.',
                                  ]}
                                ><span /></SettingsTooltip>
                              </Label>
                            </FormGroup>
                          </Column>
                        </Row>
                        {!communicationResponsible && (
                          <Row>
                            <Column {...RegisterColumns.fullColumn}>
                              <div className="register-partner-v2-info-box">
                                <p>
                                  Enter the details for your Partner company Sales Responsible here.
                                </p>
                                <p>
                                  The name, phone and email will be used to connect your profile to this Sales
                                  responsible person and grant edit access to your profile details to this person.
                                </p>
                                <p>
                                  All communication from AFRY, via email and phone, will be directed to the
                                  sales responsible.
                                </p>
                              </div>
                            </Column>
                          </Row>
                        )}
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="salesManager">Sales responsible</Label>
                              <Input
                                name="salesManager"
                                disabled={communicationResponsible}
                                onChange={this.handleChange}
                                value={salesManager}
                                placeholder="First and last name"
                              />
                              {showErrors &&
                                !Validation.Partner.salesManager(
                                  communicationResponsible,
                                  salesManager
                                ) && (
                                  <FormFeedback>Please enter your sales responsible</FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                          <Column {...RegisterColumns.rightColumn}>
                            <FormGroup>
                              <Label for="phoneMobileSalesManager">
                                Sales responsible phone
                              </Label>
                              <PhoneInput
                                onValidChange={this.handleSalesPhoneValid}
                                value={phoneMobileSalesManager}
                                onChange={this.handleSalesPhoneChange}
                                disabled={communicationResponsible}
                              />
                              {showErrors &&
                                !Validation.Partner.phoneMobileSalesManager(
                                  communicationResponsible,
                                  phoneMobileSalesValid === PhoneValidMsg.valid
                                ) && (
                                  <FormFeedback>
                                    Please enter phone for sales responsible
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>
                        <Row>
                          <Column {...RegisterColumns.leftColumn}>
                            <FormGroup>
                              <Label for="emailSalesManager">Sales responsible email</Label>
                              <Input
                                name="emailSalesManager"
                                onChange={this.handleChange}
                                value={emailSalesManager}
                                disabled={communicationResponsible}
                              />
                              {showErrors &&
                                !Validation.Partner.emailSalesManager(
                                  communicationResponsible,
                                  emailSalesManager
                                ) && (
                                  <FormFeedback>
                                    Please enter a valid email for sales responsible
                                  </FormFeedback>
                                )}
                            </FormGroup>
                          </Column>
                        </Row>

                        {/* ── Privacy notice + Submit ── */}
                        <Row>
                          <Column {...RegisterColumns.fullColumn}>
                            <div className="register-partner-v2-agreement-box">
                              <Label for="privacyStatement" className="register-partner-v2-agreement-label">
                                <Checkbox
                                  id="privacyStatement"
                                  name="privacyStatement"
                                  checked={privacyStatement}
                                  onChange={this.handleChange}
                                />
                                {' '}I have read and agree to the following
                              </Label>
                              <ul className="register-partner-v2-agreement-list">
                                <li>
                                  <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={privacyStatementForNetworkers}
                                  >
                                    AFRY Privacy notice
                                  </a>
                                </li>
                                <li>Terms and conditions / code of conduct</li>
                              </ul>
                              {showErrors && !privacyStatement && (
                                <FormFeedback>
                                  You must accept to proceed
                                </FormFeedback>
                              )}
                            </div>
                            {portalUrl !== 'https://portalstage.afry.cloud/' && (
                              <FormGroup>
                                <GoogleRecaptcha
                                  onCaptchaVerified={this.handleCaptchaSuccess}
                                />
                                {showErrors && !captchaCompleted && (
                                  <FormFeedback>reCaptcha not valid</FormFeedback>
                                )}
                              </FormGroup>
                            )}
                            <div className="register-partner-v2-submit">
                              <Button
                                color="success"
                                disabled={!privacyStatement}
                                onClick={this.handleSubmit}
                              >
                                Submit
                              </Button>
                            </div>
                          </Column>
                        </Row>
                      </>
                    )}
                  </Form>
                )}
                <RegisterError />
              </div>
            </Column>
          </Row>
        </Container>
      </div>
    );
  }
}

RegisterPartnerV2.propTypes = propTypes;
RegisterPartnerV2.defaultProps = defaultProps;

function withParams(Component) {
  return (props) => <Component {...props} params={useParams()} />;
}

export default withParams(RegisterPartnerV2);
